import React from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from 'motion/react';
import { Linkedin } from 'lucide-react';
import { Separator } from "@/components/ui/separator";
import { scrollToTop } from "@/components/ScrollToTop";
import { trackCtaClick } from "@/lib/ctaTracking";
import sorvaineLogo from "@/assets/sorvaine-footer-logo.png";
import sorvainLogoLight from "@/assets/sorvaine-footer-logo-light.png";
import { NewsletterSubscribe } from "@/components/landing/NewsletterSubscribe";

interface FooterLink {
  title: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface FooterSection {
  label: string;
  links: FooterLink[];
}

const footerLinks: FooterSection[] = [
  {
    label: 'Platform',
    links: [
      { title: 'Log in', href: '/auth' },
      { title: 'Book a Demo', href: '/book-demo' },
    ],
  },
  {
    label: 'Company',
    links: [
      { title: 'About', href: '/about' },
      { title: 'Blog', href: '/blog' },
      { title: 'Contact', href: '/contact' },
      { title: 'Security', href: '/security' },
      { title: 'Help Center', href: '/help' },
    ],
  },
  {
    label: 'Legal',
    links: [
      { title: 'Privacy Policy', href: '/privacy' },
      { title: 'Terms of Service', href: '/terms' },
      { title: 'Cookie Policy', href: '/cookies' },
      { title: 'Unsubscribe', href: '/unsubscribe' },
    ],
  },
  {
    label: 'Social',
    links: [
      { title: 'LinkedIn', href: 'https://www.linkedin.com/company/sorvaine/', icon: Linkedin },
    ],
  },
];

interface FooterProps {
  variant?: "dark" | "light";
}

export function Footer({ variant = "dark" }: FooterProps) {
  const isLight = variant === "light";
  const logo = isLight ? sorvainLogoLight : sorvaineLogo;

  return (
    <footer className={`relative z-10 ${isLight ? "bg-white" : "landing-footer"}`}>
      <div className="max-w-[1200px] mx-auto px-6">
        <Separator className={isLight ? "bg-gray-200" : "bg-white/[0.06]"} />

        {/* Newsletter band (full-width row above link columns) */}
        <AnimatedContainer delay={0.05}>
          <div className="py-4 md:py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className={`text-sm font-semibold ${isLight ? "text-gray-900" : "text-foreground"}`}>
                Get the playbook
              </h2>
              <p className={`text-sm ${isLight ? "text-gray-600" : "text-white/60"}`}>
                Monthly bid &amp; tender tactics — no spam.
              </p>
              <a
                href="https://sorvaine.beehiiv.com"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1 text-xs transition-colors ${isLight ? "text-gray-500 hover:text-gray-900" : "text-white/50 hover:text-foreground"}`}
              >
                Past issues <span aria-hidden>→</span>
              </a>
            </div>
            <div className="w-full md:w-[360px] shrink-0">
              <NewsletterSubscribe variant={variant} compact />
            </div>
          </div>

        </AnimatedContainer>

        <Separator className={isLight ? "bg-gray-200" : "bg-white/[0.06]"} />

        <div className="py-8 md:py-12">
          <div className="flex flex-col md:flex-row md:items-start gap-8 md:gap-12">
            {/* Left: Logo + Copyright */}
            <AnimatedContainer delay={0.1}>
              <div className="shrink-0 md:w-[220px] text-left md:pt-[14px]">
                <img
                  src={logo}
                  alt="Sorvaine logo"
                  width={isLight ? 156 : 109}
                  height={isLight ? 38 : 26}
                  className={`${isLight ? "h-6 md:h-10" : "h-5 md:h-7"} w-auto object-contain mb-3 md:mb-5`}
                  loading="lazy"
                  decoding="async"
                />

                <p className={`text-sm leading-relaxed ${isLight ? "text-gray-600" : "text-white/70"}`}>
                  © {new Date().getFullYear()} Sorvaine - FZCO.
                  <br />
                  All rights reserved.
                </p>
                <p className={`text-xs mt-3 leading-relaxed ${isLight ? "text-gray-500" : "text-white/50"}`}>
                  Support:{" "}
                  <a
                    href="mailto:support@sorvaine.com"
                    className={`transition-colors ${isLight ? "text-gray-600 hover:text-gray-900" : "text-white/70 hover:text-foreground"}`}
                  >
                    support@sorvaine.com
                  </a>
                  <br />
                  Replies within 1 business day
                </p>
              </div>
            </AnimatedContainer>

            {/* Right: Link columns */}
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 md:pt-[18px]">
              {footerLinks.map((section, index) => (
                <AnimatedContainer key={section.label} delay={0.1 + index * 0.05}>
                  <div>
                    <h2 className={`text-sm font-semibold mb-3 md:mb-4 ${isLight ? "text-gray-900" : "text-foreground"}`}>
                      {section.label}
                    </h2>
                    <ul className="space-y-2 md:space-y-3">
                      {section.links.map((link) => {
                        const isExternal = link.href.startsWith('http');
                        const iconOnly = isExternal && !!link.icon;
                        const baseClass = `inline-flex items-center gap-2 text-sm transition-colors duration-300 ${isLight ? "text-gray-400 hover:text-gray-900" : "text-muted-foreground/40 hover:text-foreground"}`;
                        const content = iconOnly ? (
                          <link.icon className="h-5 w-5" />
                        ) : (
                          <>
                            {link.icon && <link.icon className="h-4 w-4" />}
                            {link.title}
                          </>
                        );

                        if (isExternal) {
                          return (
                            <li key={link.title}>
                              <a
                                href={link.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={link.title}
                                className={baseClass}
                              >
                                {content}
                              </a>
                            </li>
                          );
                        }

                        return (
                          <li key={link.title}>
                            <Link
                              to={link.href}
                              onClick={() => {
                                if (link.href === '/book-demo') {
                                  void trackCtaClick('book_demo', 'footer');
                                }
                                scrollToTop();
                              }}
                              className={baseClass}
                            >
                              {content}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </AnimatedContainer>
              ))}
            </div>
          </div>

        </div>

      </div>
    </footer>
  );
}

type ViewAnimationProps = {
  delay?: number;
  className?: ComponentProps<'div'>['className'];
  children: ReactNode;
};

function AnimatedContainer({ className, delay = 0.1, children }: ViewAnimationProps) {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <>{children}</>;
  }

  return (
    <motion.div
      initial={false}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
